import { ParamsTimeType } from '@/pages/opinion/types/common';
import {
  addWithoutSingleData,
  arrayToObj,
  dateTypeMap,
  getPercentageValue,
  getRationPercentValue,
  getTimeSpaceArray,
  sortTimeListFunc,
} from '@/pages/opinion/utils';
import { formatDateFunc, timeFormatObj } from '@/pages/opinion/utils/time';
import { dealwithSteamSelectedData } from '@op/utils/data';
import { groupBy } from 'lodash-es';
import moment from 'moment';

export const dealwithTimelineList = (
  timeline_list: { list: any; type: any }[],
  timeline_of_start_time: string,
  timeline_of_end_time: string,
  dateType: ParamsTimeType,
) => {
  let objMap: any = {
    lifetime: {},
    selected: {},
  };
  timeline_list?.forEach(({ list, type }) => {
    objMap[type] = arrayToObj(list, ({ time }) =>
      formatDateFunc(dateType)(time),
    );
  });
  const timeList = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateTypeMap[dateType],
    timeFormatObj[dateType],
  );

  let data = timeList.map((time) => {
    let {
      mentions: lifetime_reviews,
      score: lifetime_score,
      time: lifetimeTime,
      detail: lifetimeDetail,
    } = objMap.lifetime[time] || {};
    let {
      mentions: selected_reviews,
      score: selected_score,
      detail,
      time: selectedTime,
    } = objMap.selected[time] || {};

    const starMap: any = {
      star1: 0,
      star2: 0,
      star3: 0,
      star4: 0,
      star5: 0,
    };
    detail = detail || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const key in detail) {
      const element = detail[key];
      starMap['star' + key] = element;
    }

    const starLifetimeMap: any = {
      star1: 0,
      star2: 0,
      star3: 0,
      star4: 0,
      star5: 0,
    };
    for (const key in lifetimeDetail) {
      const element = lifetimeDetail[key];
      starLifetimeMap['star' + key] = element;
    }

    return {
      lifetime_reviews,
      lifetime_score,
      selected_reviews,
      selected_score,
      time: lifetimeTime || selectedTime || time,
      ...starMap,
      lifetime: starLifetimeMap,
    };
  });

  return data;
};

export const scoreChangeFunc = (
  platData: string | any[],
  key: string | number,
  startTime: string,
  endTime: string,
  isAppleSelected: any,
) => {
  const isOnlyToday =
    startTime.substring(0, 10) === endTime.substring(0, 10) &&
    startTime.substring(0, 10) === moment().format('YYYY-MM-DD');
  const isOneDay =
    moment(startTime).format('YYYY-MM-DD') ===
    moment(endTime).format('YYYY-MM-DD');
  let resultData = { startScore: 0, endScore: 0 };
  if (platData && platData?.length > 0) {
    let firstItem = isOneDay ? platData[platData?.length - 2] : platData[0]; // 如果选择的是一天，对比日期的开始日期选所选日的前一天
    let lastItem = platData[platData?.length - 1];

    if (isOnlyToday && isAppleSelected) {
      firstItem = platData[platData?.length - 3];
      lastItem = platData[platData?.length - 2];
    }
    let tempStartScore =
      firstItem && (firstItem[key] ? Number(firstItem[key])?.toFixed(1) : 0);
    const startScore = tempStartScore;
    const tempEndScore = lastItem?.[key]
      ? Number(lastItem[key])?.toFixed(1)
      : 0;

    resultData = { startScore, endScore: Number(tempEndScore) };
  }
  return resultData;
};

export const groupByLanguage = (data?: Array<any>) => {
  return groupBy(data, (item) => {
    return item.language || 'global';
  });
};

const alertFilterMap: any = {
  lifetime: {
    recommend: 0.85,
    score: 4,
  },
  recent: {
    recommend: 0.9,
  },
  selected: {
    recommend: 0.9,
    score: 3.5,
  },
};
export const dealwithTableData = (
  languageData: {
    [key: string]: Array<any>;
  } = {},
) => {
  const result: any = {};
  for (const key in languageData) {
    const element = languageData[key];
    if (!result[key]) result[key] = { language: key };
    // 这里还要计算一个Recommended%值出来 recommend / review
    element.forEach((item) => {
      const keyBefore = `${item.channel_name}_${item.key}`;
      result[key] = ['recommend', 'review', 'score', 'sum_sentiment'].reduce(
        (pre, cur) => {
          return { ...pre, [`${keyBefore}_${cur}`]: item[cur] || 0 };
        },
        result[key],
      );

      const recommendPercentage = getPercentageValue(
        result[key][`${keyBefore}_recommend`],
        result[key][`${keyBefore}_review`],
        8,
      );

      const currentScore = result[key][`${keyBefore}_score`];

      // 计算是否标记为红色
      result[key][`${keyBefore}_recommend_percentage`] = recommendPercentage;

      const filter = alertFilterMap[item.key];
      const { recommend, score } = filter || {};

      if (recommend) {
        result[key][`${keyBefore}_recommendFlag`] =
          recommendPercentage < recommend;
      }

      if (score) {
        result[key][`${keyBefore}_scoreFlag`] = currentScore < score;
      }
    });
  }
  return result;
};

/**
 * TODO: 其他系统使用
 * 转换统计图数据
 * @param timelineProps
 * @returns
 */
export const dealwithPcConsoleTimelineList = (timelineProps?: {
  timeline: Array<any>;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  date_type: ParamsTimeType;
}) => {
  if (!timelineProps) return {};

  const {
    timeline = [],
    timeline_of_start_time,
    timeline_of_end_time,
    date_type = 'daily',
  } = timelineProps;

  const languageData = groupByLanguage(timeline);

  const result: any = {};
  for (const key in languageData) {
    const element = languageData[key];

    const channelTypeMap = groupBy(
      element,
      (item) => `${item.channel_name}_${item.key}`,
    );

    if (!result[key]) result[key] = {};

    for (const channelKey in channelTypeMap) {
      const element = channelTypeMap[channelKey];
      result[key][channelKey] = addWithoutSingleData(
        timeline_of_start_time,
        timeline_of_end_time,
        date_type,
        element,
        {
          recommend: 0,
          review: 0,
          score: 0,
          sum_sentiment: 0,
        },
      );
    }
  }
  return result;
};

/**
 * 处理为图表可用数据，并且补充环比数据以及环比时间
 * @param list
 * @param key
 * @param isSingleDate
 * @returns
 */
function dealwithTimelineAndAddData(
  list: Array<any>,
  key: string,
  isSingleDate: boolean,
) {
  const reviews: Array<number> = [];
  const recommendsPercentage: Array<number> = [];
  const score: Array<number> = [];
  const x: Array<number> = [];

  list.forEach((item) => {
    reviews.push(item.review);
    score.push(item.score);
    x.push(item.time);
    const recommendValue = getPercentageValue(item.recommend, item.review, 8);
    recommendsPercentage.push(recommendValue);
  });

  let beforeIndex = 0;
  if (isSingleDate) {
    beforeIndex = list.length - 2;
  }
  const currentIndex = list.length - 1;

  const newRecommendValue = recommendsPercentage[currentIndex];
  const oldRecommendValue = recommendsPercentage[beforeIndex];

  const recommend_rationPercent = getRationPercentValue(
    newRecommendValue,
    oldRecommendValue,
    2,
  );

  const newScoreValue = score[currentIndex];
  const oldScoreValue = score[beforeIndex];
  const score_rationPercent = getRationPercentValue(
    newScoreValue,
    oldScoreValue,
    2,
  );

  return {
    [`${key}_timeLine`]: { reviews, score, x, recommendsPercentage },
    [`${key}_newTime`]: [x[currentIndex]],
    [`${key}_oldTime`]: [x[beforeIndex]],
    [`${key}_newRecommend`]: newRecommendValue,
    [`${key}_oldRecommend`]: oldRecommendValue,
    [`${key}_compareRecommend`]: newRecommendValue - oldRecommendValue,
    [`${key}_recommend_rationPercent`]: recommend_rationPercent,
    [`${key}_newScore`]: newScoreValue,
    [`${key}_oldScore`]: oldScoreValue,
    [`${key}_compareScore`]: newScoreValue - oldScoreValue,
    [`${key}_score_rationPercent`]: score_rationPercent,
  };
}

function mergeTimelineAndTable(
  tableDataObj: { [key: string]: any },
  timeLineObj: { [key: string]: Array<any> },
  isSingleDate: boolean,
) {
  const result: Array<any> = [];
  Object.keys(tableDataObj).forEach((key) => {
    const timeLine = timeLineObj[key] || [];
    let table = tableDataObj[key];

    for (const timeLineKey in timeLine) {
      const element = timeLine[timeLineKey];
      const timelineData = dealwithTimelineAndAddData(
        element,
        timeLineKey,
        isSingleDate,
      );
      table = {
        ...table,
        ...timelineData,
      };
    }
    result.push(table);
  });

  return result;
}

export const dealwithToTableData = (
  list?: Array<any>,
  timelineProps?: {
    timeline: Array<any>;
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    date_type: ParamsTimeType;
  },
  isSingleDate = false,
) => {
  const languageData = groupByLanguage(list);
  const tableData = dealwithTableData(languageData);
  const timelineData = dealwithPcConsoleTimelineList(timelineProps);
  return mergeTimelineAndTable(tableData, timelineData, isSingleDate);
};

export const dealwithPcConsoleState = (data: Array<any> = []) => {
  const globalResult: any = {
    opencritic: {},
    metacritic: {},
    steam: {},
  };
  const realData = Array.isArray(data) ? data : [];
  realData?.forEach((item: any) => {
    const { channel, has_date_time, link_of_end_time, link_of_start_time } =
      item;
    let { list, old_list } = item;

    if (channel === 'steam') {
      list = dealwithSteamSelectedData(list);
      old_list = dealwithSteamSelectedData(old_list);
    }

    globalResult[channel] = {
      has_date_time,
      list: arrayToObj(list || [], 'key'),
      old_list: arrayToObj(old_list || [], 'key'),
      link_of_end_time,
      link_of_start_time,
    };
  });

  return globalResult;
};

export function defaultTimelineData(
  timeline_of_start_time = '',
  timeline_of_end_time = '',
) {
  return {
    opencritic: {
      timeline_of_start_time,
      timeline_of_end_time,
      list: {},
      old_list: {},
    },
    metacritic: {
      timeline_of_start_time,
      timeline_of_end_time,
      list: {},
      old_list: {},
    },
    steam: {
      timeline_of_start_time,
      timeline_of_end_time,
      list: {},
      old_list: {},
    },
  };
}

export function getTimeline(timeline: any) {
  const info: any = {};
  const list = timeline || [];
  list.sort(sortTimeListFunc('time'));
  list.forEach((item: any) => {
    if (!info[item.key]) {
      info[item.key] = [];
    }
    // 0不链接处理
    if (item.key === 'select_recommend_score' && item.score === 0) {
      const recommend = list.find((i: any) => i.key === 'select_recommend' && i.time === item.time);
      const non_recommend = list.find((i: any) => i.key === 'select_non_recommend' && i.time === item.time);
      if (!recommend?.review && !non_recommend?.review) {
        item.score = null;
      }
    }
    info[item.key].push(item);
  });
  return info;
}

export const dealwithPcConsoleTimeline = (
  timeline: Array<any> = [],
  startTime = '',
  endTime = '',
) => {
  const localTrendsTimeline: any = defaultTimelineData(startTime, endTime);
  timeline = Array.isArray(timeline) ? timeline : [];
  timeline.forEach((data: any) => {
    const { timeline, channel, old_time_line } = data || {};
    const info = localTrendsTimeline[channel];
    if (info) {
      info.list = getTimeline(timeline);
      info.old_list = getTimeline(old_time_line);
    }
  });

  return localTrendsTimeline;
};
